import { useMemo, useState, type Ref } from 'react';
import {
  BulbOutlined,
  CloseOutlined,
  CloudDownloadOutlined,
  CodeOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileTextOutlined,
  RetweetOutlined,
  SearchOutlined,
  SwapOutlined,
  ToolOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  ACTIVITY_STATUS_COMPLETED,
  ACTIVITY_STATUS_FAILED,
  ACTIVITY_STATUS_IN_PROGRESS,
  ACTIVITY_STATUS_PENDING,
  ACTIVITY_STATUS_WAITING,
  useAgentActivityStore,
  type AgentActivityEntry,
  type AgentPanelState,
} from '../stores/useAgentActivityStore';
import StopTurnButton, { type StopOutcome } from './StopTurnButton';
import type { ActivePerson } from '../lib/channel-activity';
import type { AgentActivityPlanEntry } from '../types/ws-frames';

const STATUS_WORD: Record<ActivePerson['status'], string> = {
  waiting: 'Waiting for you',
  working: 'Working',
  typing: 'Typing',
};

/**
 * One block per agent, over the message list.
 *
 * It floats rather than growing beside the list: a sibling that grows shrinks
 * the list's visible box while the browser keeps its scroll offset, moving a
 * reader away from the newest message without their having scrolled, and nothing
 * in the list's scroll handling compensates for a sibling changing size. The
 * thread panel scrolls a different element from the channel view, and floating
 * costs neither of them a pixel.
 *
 * Only this component and its blocks read the activity map, and they exist only
 * while the panel is open, so a report about work nobody is looking at costs one
 * string comparison in the collapsed line and nothing else.
 */
export default function ActivityPanel({
  channelId,
  panelId,
  agents,
  outcomes,
  onOutcome,
  onClose,
  panelRef,
  stopInBlocks,
}: {
  channelId: string;
  panelId: string;
  agents: ActivePerson[];
  outcomes: ReadonlyMap<string, StopOutcome>;
  onOutcome: (agentId: string, outcome: StopOutcome) => void;
  onClose: () => void;
  panelRef: Ref<HTMLDivElement>;
  /** False while the bar already offers the one agent's stop; the action is offered once. */
  stopInBlocks: boolean;
}) {
  return (
    <div
      className="activity-panel"
      id={panelId}
      ref={panelRef}
      role="region"
      aria-label="Active now"
      tabIndex={-1}
    >
      <div className="activity-panel-header">
        <span className="activity-panel-title">Active now</span>
        <button type="button" className="activity-panel-close" aria-label="Close" onClick={onClose}>
          <CloseOutlined />
        </button>
      </div>
      {agents.map(agent => (
        <AgentBlock
          key={agent.id}
          channelId={channelId}
          person={agent}
          outcome={outcomes.get(agent.id) ?? 'idle'}
          onOutcome={onOutcome}
          showStop={stopInBlocks}
        />
      ))}
    </div>
  );
}

/**
 * What one agent plans to do, what it is doing, and what it is saying, in the
 * order a reader wants them. A section with nothing in it is absent rather than
 * rendered empty, so an agent that has said nothing yet is its header alone —
 * which is a block worth having only when the bar cannot carry its stop button,
 * and that is exactly when the panel opens for it.
 *
 * Each block subscribes to its own agent, so a report about one leaves the other
 * blocks untouched.
 */
function AgentBlock({
  channelId,
  person,
  outcome,
  onOutcome,
  showStop,
}: {
  channelId: string;
  person: ActivePerson;
  outcome: StopOutcome;
  onOutcome: (agentId: string, outcome: StopOutcome) => void;
  showStop: boolean;
}) {
  const agent = useAgentActivityStore(s => s.byChannel[channelId]?.[person.id]);

  return (
    <section className="activity-agent">
      <header className="activity-agent-header">
        <span className="activity-agent-name">{person.name}</span>
        <span className="activity-agent-line" data-line={person.status}>
          {STATUS_WORD[person.status]}
        </span>
        {showStop && (
          <StopTurnButton
            className="activity-stop-btn"
            channelId={channelId}
            agentId={person.id}
            displayName={person.name}
            outcome={outcome}
            onOutcome={onOutcome}
          />
        )}
      </header>
      {agent && <AgentSections agent={agent} />}
    </section>
  );
}

function AgentSections({ agent }: { agent: AgentPanelState }) {
  return (
    <>
      {agent.plan.length > 0 && <PlanSection plan={agent.plan} />}
      {agent.activities.length > 0 && <ActivitySection activities={agent.activities} />}
      {agent.answer !== '' && <OutputSection text={agent.answer} />}
    </>
  );
}

/**
 * The plan as a summary, with the whole list one click away.
 *
 * A plan can run to a dozen entries and only one of them is what the agent is
 * doing now, so the summary is what belongs in a panel a reader glances at.
 * Rows are keyed by position because the protocol gives entries no identity and
 * resends the list whole; the store keeps an unchanged entry's own object, so a
 * status flipping somewhere in the list leaves the rest of the rows alone.
 */
function PlanSection({ plan }: { plan: AgentActivityPlanEntry[] }) {
  const [showAll, setShowAll] = useState(false);
  const current = plan.find(entry => entry.status === 'in_progress');
  const done = plan.filter(entry => entry.status === 'completed').length;
  const left = plan.length - done - (current ? 1 : 0);

  return (
    <div className="activity-section">
      <button
        type="button"
        className="activity-plan-summary"
        aria-expanded={showAll}
        onClick={() => setShowAll(open => !open)}
      >
        {current ? (
          <>
            <span className="activity-plan-current">{current.label}</span>
            {left > 0 && <span className="activity-plan-rest">{left} to go</span>}
          </>
        ) : (
          <span className="activity-plan-rest">{done} of {plan.length} done</span>
        )}
        <span className="activity-plan-toggle" aria-hidden="true">
          {showAll ? <UpOutlined /> : <DownOutlined />}
        </span>
      </button>
      {showAll && (
        <ol className="activity-plan-list">
          {plan.map((entry, index) => (
            <li key={index} className="activity-plan-item" data-status={entry.status}>
              {entry.label}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * The tool calls, with a sub-agent's work indented under the call that spawned
 * it.
 *
 * A child can arrive before its parent — a permission request surfaces a call
 * before its own stream does — so an entry whose parent is not held yet renders
 * at the top level and moves under the parent once that arrives. Nothing is
 * dropped for want of a parent, including an entry caught in a cycle of parent
 * ids, which no traversal from the roots would reach.
 */
function ActivitySection({ activities }: { activities: AgentActivityEntry[] }) {
  const tree = useMemo(() => buildActivityTree(activities), [activities]);
  return (
    <ol className="activity-list">
      {tree.map(({ entry, children }) => (
        <li key={entry.id} className="activity-item-group">
          <ActivityRow entry={entry} />
          {children.length > 0 && (
            <ol className="activity-list activity-list-nested">
              {children.map(child => (
                <li key={child.id}>
                  <ActivityRow entry={child} />
                </li>
              ))}
            </ol>
          )}
        </li>
      ))}
    </ol>
  );
}

interface ActivityNode {
  entry: AgentActivityEntry;
  children: AgentActivityEntry[];
}

/**
 * Groups the entries one level deep: everything descended from a top-level
 * entry sits directly under it, however many parent links away it is.
 */
function buildActivityTree(activities: AgentActivityEntry[]): ActivityNode[] {
  const held = new Map(activities.map(entry => [entry.id, entry]));
  const nodes: ActivityNode[] = [];
  const byRoot = new Map<string, ActivityNode>();

  for (const entry of activities) {
    if (entry.parentId === '' || !held.has(entry.parentId)) {
      const node: ActivityNode = { entry, children: [] };
      nodes.push(node);
      byRoot.set(entry.id, node);
    }
  }

  for (const entry of activities) {
    if (byRoot.has(entry.id)) continue;
    let ancestor = held.get(entry.parentId);
    for (let hops = 0; ancestor && !byRoot.has(ancestor.id) && hops < activities.length; hops += 1) {
      ancestor = held.get(ancestor.parentId);
    }
    const root = ancestor && byRoot.get(ancestor.id);
    if (root) root.children.push(entry);
    else nodes.push({ entry, children: [] });
  }

  return nodes;
}

/**
 * One tool call.
 *
 * The description is preferred over the label because they are two strings with
 * two jobs: for a shell tool the label is the protocol's title, which is the raw
 * command line, and the description is what the agent said it was doing, which
 * is what a reader glancing at a panel wants. An entry with neither is a patch
 * to something never seen, and its reason is the only thing it has to say.
 */
function ActivityRow({ entry }: { entry: AgentActivityEntry }) {
  const Icon = ACTIVITY_KIND_ICONS[entry.kind] ?? ToolOutlined;
  const title = entry.description || entry.label;
  const status = ACTIVITY_STATUS_LABELS[entry.status] ?? entry.status;
  const shown = entry.paths.slice(0, PATHS_SHOWN);

  return (
    <div className="activity-item" data-status={entry.status}>
      <span className="activity-item-icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="activity-item-body">
        <span className="activity-item-line">
          {title !== '' && <span className="activity-item-title">{title}</span>}
          {entry.subagent && <span className="activity-item-badge">sub-agent</span>}
          <span className="activity-item-status">{status}</span>
        </span>
        {entry.detail !== '' && <span className="activity-item-detail">{entry.detail}</span>}
        {shown.length > 0 && (
          <span className="activity-item-detail">
            {shown.join(', ')}
            {entry.paths.length > shown.length && ` +${entry.paths.length - shown.length}`}
          </span>
        )}
        {entry.reason !== '' && <span className="activity-item-reason">{entry.reason}</span>}
      </span>
    </div>
  );
}

/**
 * The tail of what the agent is saying.
 *
 * Plain text, never markdown: the answer arrives as a message of its own when
 * the turn ends and that is where it is rendered, while this changes several
 * times a second and parsing it that often would cost more than the whole rail
 * saves. It also keeps `dangerouslySetInnerHTML` off a surface fed straight
 * from an agent.
 *
 * The box is a fixed number of lines, so text that grows and shrinks does not
 * move the panel under the reader, and the newest line is the one pinned in
 * view rather than the oldest.
 */
function OutputSection({ text }: { text: string }) {
  return (
    <div className="activity-output">
      <span className="activity-output-text">{text}</span>
    </div>
  );
}

/** How many of an activity's files are named before the rest become a count. */
const PATHS_SHOWN = 3;

/**
 * The agent protocol's ten tool kinds. The enum is open — the next protocol
 * version makes that mandatory — so anything absent from this map is a tool
 * like any other and gets the generic icon.
 */
const ACTIVITY_KIND_ICONS: Record<string, typeof ToolOutlined> = {
  read: FileTextOutlined,
  edit: EditOutlined,
  delete: DeleteOutlined,
  move: SwapOutlined,
  search: SearchOutlined,
  execute: CodeOutlined,
  think: BulbOutlined,
  fetch: CloudDownloadOutlined,
  switch_mode: RetweetOutlined,
  other: ToolOutlined,
};

/** An unrecognised status shows the word the agent sent rather than being renamed or hidden. */
const ACTIVITY_STATUS_LABELS: Record<string, string> = {
  [ACTIVITY_STATUS_PENDING]: 'Queued',
  [ACTIVITY_STATUS_IN_PROGRESS]: 'Running',
  [ACTIVITY_STATUS_WAITING]: 'Waiting for you',
  [ACTIVITY_STATUS_COMPLETED]: 'Done',
  [ACTIVITY_STATUS_FAILED]: 'Failed',
};
